import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Save, Edit2, X, Briefcase, Eye, EyeOff, Download, Users } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CareersPanel = ({ token }) => {
  const [activeTab, setActiveTab] = useState('jobs');
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingJob, setEditingJob] = useState(null);
  const [newJob, setNewJob] = useState(null);
  const [saving, setSaving] = useState(false);

  const emptyJob = {
    title: '',
    designation: '',
    experience_min: 0,
    experience_max: 5,
    location: '',
    technologies: [],
    employment_type: 'Full-time',
    role_summary: '',
    responsibilities: [],
    requirements: [],
    nice_to_have: [],
    benefits: [],
    salary_range: '',
    is_active: true,
    order: 0
  };

  useEffect(() => {
    fetchJobs();
    fetchApplications();
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await fetch(`${API_URL}/api/careers/jobs?active_only=false`);
      if (response.ok) {
        const data = await response.json();
        setJobs(data);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const fetchApplications = async () => {
    try {
      const response = await fetch(`${API_URL}/api/careers/applications`);
      if (response.ok) {
        const data = await response.json();
        setApplications(data);
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
    }
  };

  const handleSaveJob = async (jobData, isNew = false) => {
    setSaving(true);
    try {
      const url = isNew 
        ? `${API_URL}/api/careers/jobs`
        : `${API_URL}/api/careers/jobs/${jobData.id}`;
      
      const response = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(jobData)
      });

      if (response.ok) {
        toast.success(isNew ? 'Job created successfully' : 'Job updated successfully');
        fetchJobs();
        setEditingJob(null);
        setNewJob(null);
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save job');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this job?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/careers/jobs/${jobId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success('Job deleted successfully');
        fetchJobs();
      } else {
        throw new Error('Failed to delete');
      }
    } catch (error) {
      toast.error('Failed to delete job');
    }
  };

  const handleToggleActive = async (job) => {
    try {
      const response = await fetch(`${API_URL}/api/careers/jobs/${job.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !job.is_active })
      });

      if (response.ok) {
        toast.success(`Job ${job.is_active ? 'deactivated' : 'activated'}`);
        fetchJobs();
      }
    } catch (error) {
      toast.error('Failed to update job status');
    }
  };

  const handleDownloadResume = (applicationId, filename) => {
    window.open(`${API_URL}/api/careers/applications/${applicationId}/resume`, '_blank');
  };

  const handleUpdateApplicationStatus = async (applicationId, status) => {
    try {
      const response = await fetch(
        `${API_URL}/api/careers/applications/${applicationId}/status?status=${status}`,
        { method: 'PUT' }
      );

      if (response.ok) {
        toast.success('Status updated');
        fetchApplications();
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const parseArrayField = (value) => {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      return value.split('\n').filter(item => item.trim());
    }
    return [];
  };

  const JobForm = ({ job, onSave, onCancel, isNew }) => {
    const [formData, setFormData] = useState({
      ...job,
      technologies: job.technologies?.join(', ') || '',
      responsibilities: job.responsibilities?.join('\n') || '',
      requirements: job.requirements?.join('\n') || '',
      nice_to_have: job.nice_to_have?.join('\n') || '',
      benefits: job.benefits?.join('\n') || ''
    });

    const handleSubmit = () => {
      const dataToSave = {
        ...formData,
        technologies: formData.technologies.split(',').map(t => t.trim()).filter(Boolean),
        responsibilities: parseArrayField(formData.responsibilities),
        requirements: parseArrayField(formData.requirements),
        nice_to_have: parseArrayField(formData.nice_to_have),
        benefits: parseArrayField(formData.benefits),
        experience_min: parseInt(formData.experience_min) || 0,
        experience_max: parseInt(formData.experience_max) || 5
      };
      onSave(dataToSave, isNew);
    };

    return (
      <Card className="border-2 border-[#320547]">
        <CardContent className="pt-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Job Title *</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Senior Full Stack Developer"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Designation *</label>
              <Input
                value={formData.designation}
                onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                placeholder="Software Engineer"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Location *</label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="Bangalore, India"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Min Experience (years)</label>
              <Input
                type="number"
                value={formData.experience_min}
                onChange={(e) => setFormData({ ...formData, experience_min: e.target.value })}
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Max Experience (years)</label>
              <Input
                type="number"
                value={formData.experience_max}
                onChange={(e) => setFormData({ ...formData, experience_max: e.target.value })}
                min="0"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Employment Type</label>
              <select
                value={formData.employment_type}
                onChange={(e) => setFormData({ ...formData, employment_type: e.target.value })}
                className="w-full p-2 border rounded-md"
              >
                <option value="Full-time">Full-time</option>
                <option value="Part-time">Part-time</option>
                <option value="Contract">Contract</option>
                <option value="Internship">Internship</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Salary Range (optional)</label>
              <Input
                value={formData.salary_range || ''}
                onChange={(e) => setFormData({ ...formData, salary_range: e.target.value })}
                placeholder="₹15-25 LPA"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Technologies (comma separated)</label>
            <Input
              value={formData.technologies}
              onChange={(e) => setFormData({ ...formData, technologies: e.target.value })}
              placeholder="React, Node.js, Python, AWS"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Role Summary</label>
            <Textarea
              value={formData.role_summary}
              onChange={(e) => setFormData({ ...formData, role_summary: e.target.value })}
              placeholder="Brief description of the role..."
              rows={3}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Responsibilities (one per line)</label>
            <Textarea
              value={formData.responsibilities}
              onChange={(e) => setFormData({ ...formData, responsibilities: e.target.value })}
              placeholder="Design and develop scalable applications&#10;Collaborate with cross-functional teams&#10;Code review and mentoring"
              rows={4}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Requirements (one per line)</label>
            <Textarea
              value={formData.requirements}
              onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
              placeholder="5+ years of experience in software development&#10;Strong knowledge of React and Node.js&#10;Experience with cloud platforms"
              rows={4}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Nice to Have (one per line)</label>
            <Textarea
              value={formData.nice_to_have}
              onChange={(e) => setFormData({ ...formData, nice_to_have: e.target.value })}
              placeholder="Experience with microservices&#10;Knowledge of DevOps practices"
              rows={3}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Benefits (one per line)</label>
            <Textarea
              value={formData.benefits}
              onChange={(e) => setFormData({ ...formData, benefits: e.target.value })}
              placeholder="Competitive salary&#10;Health insurance&#10;Flexible work hours&#10;Learning & development budget"
              rows={3}
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="rounded"
            />
            <label htmlFor="is_active" className="text-sm font-medium">Active (visible on website)</label>
          </div>

          <div className="flex gap-2 pt-4">
            <Button onClick={handleSubmit} disabled={saving}>
              <Save className="mr-2" size={16} />
              {saving ? 'Saving...' : 'Save Job'}
            </Button>
            <Button variant="outline" onClick={onCancel}>
              <X className="mr-2" size={16} />
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-[#320547] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="careers-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Careers</h2>
          <p className="text-gray-500">Manage job listings and applications</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="jobs" className="flex items-center gap-2">
            <Briefcase size={16} />
            Job Listings ({jobs.length})
          </TabsTrigger>
          <TabsTrigger value="applications" className="flex items-center gap-2">
            <Users size={16} />
            Applications ({applications.length})
          </TabsTrigger>
        </TabsList>

        {/* Jobs Tab */}
        <TabsContent value="jobs" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Job Listings</h3>
            <Button onClick={() => setNewJob({ ...emptyJob, order: jobs.length })} size="sm">
              <Plus className="mr-1" size={16} /> Add Job
            </Button>
          </div>

          {newJob && (
            <JobForm
              job={newJob}
              onSave={handleSaveJob}
              onCancel={() => setNewJob(null)}
              isNew={true}
            />
          )}

          <div className="space-y-4">
            {jobs.map((job) => (
              <Card key={job.id} className={`${!job.is_active ? 'opacity-60' : ''}`}>
                <CardContent className="p-4">
                  {editingJob?.id === job.id ? (
                    <JobForm
                      job={editingJob}
                      onSave={handleSaveJob}
                      onCancel={() => setEditingJob(null)}
                      isNew={false}
                    />
                  ) : (
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="font-semibold text-lg text-[#320547]">{job.title}</h4>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${
                            job.is_active 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {job.is_active ? 'Active' : 'Inactive'}
                          </span>
                          <span className="px-2 py-0.5 text-xs bg-[#320547]/10 text-[#320547] rounded-full">
                            {job.employment_type}
                          </span>
                        </div>
                        <p className="text-[#95228A] font-medium mb-2">{job.designation}</p>
                        <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                          <span>📍 {job.location}</span>
                          <span>⏱️ {job.experience_min}-{job.experience_max} years</span>
                          {job.technologies?.length > 0 && (
                            <span>💻 {job.technologies.slice(0, 3).join(', ')}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleActive(job)}
                          title={job.is_active ? 'Deactivate' : 'Activate'}
                        >
                          {job.is_active ? <EyeOff size={16} /> : <Eye size={16} />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingJob(job)}
                        >
                          <Edit2 size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleDeleteJob(job.id)}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {jobs.length === 0 && !newJob && (
            <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg">
              <Briefcase size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="mb-2">No job listings yet</p>
              <p className="text-sm">Click "Add Job" to create your first job listing</p>
            </div>
          )}
        </TabsContent>

        {/* Applications Tab */}
        <TabsContent value="applications" className="space-y-4">
          <h3 className="text-lg font-semibold">Job Applications</h3>

          {applications.length === 0 ? (
            <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg">
              <Users size={48} className="mx-auto text-gray-300 mb-4" />
              <p>No applications received yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {applications.map((app) => (
                <Card key={app.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="font-semibold text-[#320547]">{app.name}</h4>
                          <select
                            value={app.status}
                            onChange={(e) => handleUpdateApplicationStatus(app.id, e.target.value)}
                            className={`px-2 py-0.5 text-xs rounded-full border-0 ${
                              app.status === 'new' ? 'bg-blue-100 text-blue-700' :
                              app.status === 'reviewing' ? 'bg-yellow-100 text-yellow-700' :
                              app.status === 'shortlisted' ? 'bg-green-100 text-green-700' :
                              app.status === 'interviewed' ? 'bg-purple-100 text-purple-700' :
                              app.status === 'hired' ? 'bg-emerald-100 text-emerald-700' :
                              'bg-red-100 text-red-700'
                            }`}
                          >
                            <option value="new">New</option>
                            <option value="reviewing">Reviewing</option>
                            <option value="shortlisted">Shortlisted</option>
                            <option value="interviewed">Interviewed</option>
                            <option value="hired">Hired</option>
                            <option value="rejected">Rejected</option>
                          </select>
                        </div>
                        <p className="text-sm text-gray-600 mb-1">
                          Applied for: <span className="font-medium">{app.job_title}</span>
                        </p>
                        <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                          <span>📧 {app.email}</span>
                          <span>📱 {app.phone}</span>
                          <span>📅 {new Date(app.applied_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownloadResume(app.id, app.resume_filename)}
                        className="flex items-center gap-2"
                      >
                        <Download size={16} />
                        Resume
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CareersPanel;
